// OverviewTab — summary + deficiency/diff highlights + recent activity
// (Phase 3D). Pulls deficiency job results if a job id is known.

import { useEffect, useState, useRef } from 'react';
import { Loader2, AlertTriangle, FileText, RefreshCw, Database, Play, Mail, X, Copy, Download, UploadCloud, Unlock, Ban, AlertCircle } from 'lucide-react';
import { getDeficiencyJob, startDeficiency, getLatestDeficiency, downloadDeficiencyDocx, reconstruct, reconstructFile, type DeficiencyDocResult } from '../api/datawash';

// Placeholder pattern from the tag_whitewash system (REDACTED-{"<key>"}).
import { listDocuments, getCaseChecklist, uploadDocuments, updateCase, type DocumentItem, type CaseChecklist, type CaseFact, type IntakeDiscrepancy } from '../api/cases';
import type { Case } from '../api/cases';
import { renderMarkdown } from '../utils/markdown';
import { copyToClipboard } from '../utils/clipboard';

interface OverviewTabProps {
  caseData: Case;
  deficiencyJobId: string | null;
  whitewashJobId?: string | null;
  compareSummary?: string | null;
  refreshKey?: number;
  /** Open the Public Data tab and trigger an internet pull. */
  onPullPublicData?: () => void;
  /** Open the Public Data tab without triggering a pull. */
  onOpenPublicData?: () => void;
  /** Report a deficiency job id up so the parent can track it + we poll. */
  onDeficiencyJob?: (jobId: string) => void;
  /** Notify parent that documents changed so it can refresh. */
  onDocumentsChanged?: () => void;
  /** Case facts (for manual-override asterisk detection). */
  facts?: CaseFact[];
  /** Kick off a full rubric review (no public-data pull). */
  onRunReview?: () => void;
  /** Re-pull public data + auto-launch a review. */
  onReReview?: () => void;
  /** Cancel the in-progress review run. */
  onCancelReview?: () => void;
  /** Full-review kickoff in flight. */
  reviewLoading?: boolean;
  /** Re-pull + re-review kickoff in flight. */
  rereviewLoading?: boolean;
  /** A review run is RUNNING. */
  reviewInProgress?: boolean;
  /** Cancel-review in flight. */
  cancellingReview?: boolean;
  /** Any past review run exists on this case. */
  hasReview?: boolean;
  /** Last FAILED error message (shown as a banner). */
  reviewFailedMsg?: string | null;
  /** A RUNNING review has exceeded the stuck threshold (no terminal
   * transition). Swaps the in-progress banner for a 'stuck' banner. */
  reviewStuck?: boolean;
  /** Intake discrepancy rows for this case (read-join; surfaced for the
   * "Rejected" badge on gated identifier fields, operator 2026-11-04). */
  intakeDiscrepancies?: IntakeDiscrepancy[];
}

// Resolve the best client/entity name for email salutation.
function clientName(caseData: Case): string {
  return caseData.entity_name || caseData.issuer_name || caseData.depositor_name || caseData.title || caseData.case_number || 'the Client';
}

// Popup 1: email to client about MISSING DOCUMENTS (from checklist).
function buildMissingDocsEmail(caseData: Case, checklist: CaseChecklist | null): string {
  const name = clientName(caseData);
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const missingDocs = checklist ? checklist.required_documents.filter((d) => d.status === 'missing') : [];
  const missingInfo = checklist ? checklist.required_information.filter((d) => d.status === 'missing') : [];
  const lines: string[] = [];
  lines.push(`Subject: Missing Documents / Information — ${name}`);
  lines.push('');
  lines.push('Dear Representative,');
  lines.push('');
  lines.push(`Thank you for submitting your deposit review documents. After a preliminary review of the materials provided, we have identified the following items that are missing or incomplete and are required to complete the review:`);
  lines.push('');
  if (missingDocs.length > 0) {
    lines.push('Missing Documents:');
    for (const d of missingDocs) {
      lines.push(`  • ${d.label}${d.description ? ' — ' + d.description : ''}`);
    }
    lines.push('');
  }
  if (missingInfo.length > 0) {
    lines.push('Missing Information:');
    for (const d of missingInfo) {
      lines.push(`  • ${d.label}${d.description ? ' — ' + d.description : ''}`);
    }
    lines.push('');
  }
  if (missingDocs.length === 0 && missingInfo.length === 0) {
    lines.push('At this time, all required documents and information appear to have been provided. We will proceed with the review and contact you if any additional items are needed.');
    lines.push('');
  }
  lines.push('Please provide the above items at your earliest convenience so we can continue processing your review. If you have any questions or require clarification, please do not hesitate to contact us.');
  lines.push('');
  lines.push('Thank you for your prompt attention to this matter.');
  lines.push('');
  lines.push('Sincerely,');
  lines.push('[Your Name]');
  lines.push('[Your Title]');
  lines.push('[Your Firm]');
  lines.push('[Your Phone] | [Your Email]');
  lines.push(`Generated: ${date}`);
  return lines.join('\n');
}

// Popup 2: nicely formatted deficiency list + missing items (combined report).
// Emits structured markdown (headers, bold labels, bullet lists) so the
// popup can render it with the shared renderMarkdown() helper instead of
// showing raw ASCII-art plain text.
function buildDeficiencyReport(caseData: Case, docs: DocumentItem[], defResults: DeficiencyDocResult[], checklist: CaseChecklist | null): string {
  const name = clientName(caseData);
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const lines: string[] = [];
  lines.push(`# DEFICIENCY REPORT — ${name}`);
  lines.push(`**Case:** ${caseData.title || caseData.case_number}`);
  if (caseData.entity_name) lines.push(`**Entity:** ${caseData.entity_name}`);
  lines.push(`**Date:** ${date}`);
  lines.push('');
  // Section 1: deficiencies from the analysis. If no scan has been run,
  // fall back to showing checklist missing documents as deficiencies.
  let totalDefs = 0;
  const analysisRun = defResults.length > 0;
  if (analysisRun) {
    lines.push('## Document Deficiencies');
    lines.push('*Issues found inside uploaded documents.*');
    lines.push('');
    for (const r of defResults) {
      const doc = docs.find((d: DocumentItem) => d.id === r.document_id);
      const docName = doc?.original_filename || r.document_id || 'Document';
      const defs = r.deficiencies || [];
      totalDefs += defs.length;
      if (defs.length === 0) continue;
      lines.push(`### ${docName}`);
      for (const d of defs) {
        const sev = (d.severity || 'medium').toUpperCase();
        const loc = d.source_location ? ` — *location: ${d.source_location}*` : '';
        lines.push(`- [${sev}] ${d.description}${loc}`);
      }
      lines.push('');
    }
    if (totalDefs === 0) lines.push('No document-level deficiencies identified in the uploaded documents.');
    lines.push('');
  } else if (checklist) {
    // No scan run yet — show checklist missing items as deficiencies.
    const missingDocs = checklist.required_documents.filter((d) => d.status === 'missing');
    const missingInfo = checklist.required_information.filter((d) => d.status === 'missing');
    totalDefs = missingDocs.length + missingInfo.length;
    if (totalDefs > 0) {
      lines.push('## Deficiencies (from checklist — missing required items)');
      lines.push('');
      if (missingDocs.length > 0) {
        lines.push('### Missing Documents');
        for (const d of missingDocs) {
          lines.push(`- [HIGH] ${d.label}${d.description ? ' — ' + d.description : ''}`);
        }
        lines.push('');
      }
      if (missingInfo.length > 0) {
        lines.push('### Missing Information');
        for (const d of missingInfo) {
          lines.push(`- [MEDIUM] ${d.label}${d.description ? ' — ' + d.description : ''}`);
        }
        lines.push('');
      }
    }
  }
  // Section 2: missing items from checklist — only show separately when
  // the analysis WAS run (otherwise they're already shown as deficiencies above).
  if (analysisRun && checklist) {
    const missingDocs = checklist.required_documents.filter((d) => d.status === 'missing');
    const missingInfo = checklist.required_information.filter((d) => d.status === 'missing');
    if (missingDocs.length > 0 || missingInfo.length > 0) {
      lines.push('## Missing Items (Checklist)');
      lines.push('');
      if (missingDocs.length > 0) {
        lines.push('### Missing Documents');
        for (const d of missingDocs) {
          lines.push(`- ${d.label}${d.description ? ' — ' + d.description : ''}`);
        }
        lines.push('');
      }
      if (missingInfo.length > 0) {
        lines.push('### Missing Information');
        for (const d of missingInfo) {
          lines.push(`- ${d.label}${d.description ? ' — ' + d.description : ''}`);
        }
        lines.push('');
      }
    }
  }
  const chkMissing = checklist
    ? checklist.required_documents.filter((d) => d.status === 'missing').length + checklist.required_information.filter((d) => d.status === 'missing').length
    : 0;
  if (analysisRun) {
    if (totalDefs > 0) lines.push(`Document deficiencies (content scan): ${totalDefs}`);
    if (chkMissing > 0) lines.push(`Missing items (checklist): ${chkMissing}`);
  } else {
    if (totalDefs > 0) lines.push(`Deficiencies (from checklist): ${totalDefs}`);
  }
  return lines.join('\n');
}
export default function OverviewTab({ caseData, deficiencyJobId, whitewashJobId: _whitewashJobId, compareSummary, refreshKey, onPullPublicData, onOpenPublicData, onDeficiencyJob, onDocumentsChanged, facts, onRunReview, onReReview, onCancelReview, reviewLoading, rereviewLoading, reviewInProgress, cancellingReview, hasReview, reviewFailedMsg, reviewStuck, intakeDiscrepancies }: OverviewTabProps) {
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [defResults, setDefResults] = useState<DeficiencyDocResult[]>([]);
  const [checklist, setChecklist] = useState<CaseChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [runningDef, setRunningDef] = useState(false);
  const [defError, setDefError] = useState<string | null>(null);
  const [useLlm, setUseLlm] = useState(false);
  const [showEmailClient, setShowEmailClient] = useState(false);
  const [showDefList, setShowDefList] = useState(false);
  const [showReconstruct, setShowReconstruct] = useState(false);
  const [reconstructInput, setReconstructInput] = useState('');
  const [reconstructOutput, setReconstructOutput] = useState('');
  const [reconstructing, setReconstructing] = useState(false);
  const [reconstructError, setReconstructError] = useState<string | null>(null);
  const [reconstructLoadedFile, setReconstructLoadedFile] = useState<string | null>(null);
  const reconFileInputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [exportingDefDocx, setExportingDefDocx] = useState(false);
  const [defDocxError, setDefDocxError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [savingFacts, setSavingFacts] = useState(false);
  const [editFacts, setEditFacts] = useState({
    issuer_name: '',
    issuer_symbol: '',
    cusip: '',
    cik: '',
    depositor_name: '',
    depositor_type: '',
    account_ref: '',
    deposit_date: '',
    share_quantity: '' as string | number,
  });

  // Fields manually overridden (source_type="manual" in case_facts).
  const manualFields = new Set((facts || []).filter((f) => f.source_type === 'manual').map((f) => f.field_name));

  // --- Overview "Rejected" badge for gated identifiers (operator
  // 2026-11-04) ---
  // When the identifier gate rejects a form value at intake, the Case
  // header column is left blank + a CaseFact row holds
  // "manual-review-required" (source_type="not_verifiable") + an
  // IntakeDiscrepancy row records the rejected value. We surface an amber
  // "Rejected" badge + the rejected value + a client-side reason so the
  // overview is not silently blank. Reason mirrors the backend
  // `_identifier_rejection_reason` at a high level (no migration; reason
  // is derived client-side per the brief).
  // Match discrepancies by the case_facts field_name. Ticker is stored
  // under `issuer_symbol` in both case_facts + intake_discrepancy; the
  // Case column is `ticker` / `issuer_symbol`.
  const discrepancies = intakeDiscrepancies || [];
  const discrepancyFor = (fieldName: string): IntakeDiscrepancy | undefined =>
    discrepancies.find((d) => d.field_name === fieldName);
  // Display value for a gated identifier: the Case header column when
  // present, otherwise the CaseFact row's field_value when it carries the
  // manual-review placeholder (so the field reads "manual-review-required"
  // instead of silently blank "--").
  const factValueFor = (fieldName: string): string | undefined => {
    const f = (facts || []).find((x) => x.field_name === fieldName);
    return f && f.field_value ? f.field_value : undefined;
  };

  const resetEditFromCase = () => {
    setEditFacts({
      issuer_name: caseData.issuer_name || '',
      issuer_symbol: caseData.ticker || caseData.issuer_name || '',
      cusip: caseData.cusip || '',
      cik: caseData.cik || '',
      depositor_name: caseData.depositor_name || '',
      depositor_type: caseData.depositor_type || '',
      account_ref: caseData.account_ref || '',
      deposit_date: caseData.deposit_date || '',
      share_quantity: caseData.share_quantity != null ? String(caseData.share_quantity) : '',
    });
  };

  useEffect(() => { resetEditFromCase(); }, [caseData.id, caseData]);

  const handleReconstruct = async () => {
    setReconstructError(null);
    setReconstructOutput('');
    if (!reconstructInput.trim()) {
      setReconstructError('Paste the tagged/stripped text first.');
      return;
    }
    setReconstructing(true);
    try {
      const result = await reconstruct(caseData.id, reconstructInput);
      setReconstructOutput(result.reconstructed_text || '(empty result)');
    } catch (e: any) {
      setReconstructError(e?.response?.data?.detail || e?.message || 'Reconstruct failed.');
    } finally {
      setReconstructing(false);
    }
  };

  // Download the reconstructed text as a .md file.
  const handleReconstructDownload = () => {
    if (!reconstructOutput) return;
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    const slug = (caseData.case_number || caseData.id).replace(/[^a-zA-Z0-9_-]/g, '_');
    const filename = `reconstructed_${slug}_${ts}.md`;
    const blob = new Blob([reconstructOutput + '\n'], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load a PII-stripped document file (PDF/DOCX/TXT/MD -- the same washed
  // files the chat drop accepts) + reconstruct it server-side. The backend
  // extracts the text, detects REDACTED-{"<key>"} tokens, loads the case tag_map, and
  // returns the repopulated text. Same reconstruct-file endpoint the chat
  // uses -- the dialog is an alternate UI for the same flow.
  const handleReconstructFile = async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    const f = fileArr[0];
    setReconstructError(null);
    setReconstructOutput('');
    setReconstructLoadedFile(f.name);
    setReconstructing(true);
    try {
      const result = await reconstructFile(caseData.id, f);
      if (result.reconstructed) {
        setReconstructOutput(result.reconstructed_text || '(reconstruction yielded empty text)');
        // Clear the paste textarea -- the file's content was processed
        // server-side; the paste Reconstruct button stays disabled (empty
        // input) so the user doesn't accidentally re-run paste-reconstruct on
        // a non-tag placeholder string.
      } else {
        // reason: 'no_tags' | 'no_text' -- the file has no PII tags, so
        // there is nothing to reconstruct.
        const reason = result.reason === 'no_text'
          ? 'No text could be extracted from this file.'
          : 'This document has no PII tags (REDACTED-{"<key>"} tokens) to reconstruct. Drop a PII-stripped / data-washed file.';
        setReconstructError(reason);
        setReconstructLoadedFile(null);
      }
    } catch (e: any) {
      setReconstructError(e?.response?.data?.detail || e?.message || 'Reconstruct failed.');
      setReconstructLoadedFile(null);
    } finally {
      setReconstructing(false);
    }
  };

  const onReconFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleReconstructFile(e.target.files);
    e.target.value = '';
  };

  // (The cross-tab reconstruct-file handoff from the Documents drop
  // zone was removed when the Documents-tab drop migrated to the durable
  // case_request ledger -- PII-tagged files are now detected +
  // reconstructed server-side + the text is surfaced in the chat.)

  const handleSaveFacts = async () => {
    setSavingFacts(true);
    try {
      await updateCase(caseData.id, {
        issuer_name: editFacts.issuer_name,
        issuer_symbol: editFacts.issuer_symbol,
        cusip: editFacts.cusip,
        cik: editFacts.cik,
        depositor_name: editFacts.depositor_name,
        depositor_type: editFacts.depositor_type,
        account_ref: editFacts.account_ref,
        deposit_date: editFacts.deposit_date ? String(editFacts.deposit_date) : undefined,
        share_quantity: editFacts.share_quantity !== '' && editFacts.share_quantity != null ? Number(editFacts.share_quantity) : undefined,
      });
      setEditing(false);
      onDocumentsChanged?.();
    } catch (e: any) {
      setUploadError(e?.response?.data?.detail || e?.message || 'Failed to save');
    } finally {
      setSavingFacts(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listDocuments(caseData.id).then((r) => r.items || []).catch(() => [] as DocumentItem[]),
      // Load latest auto-scanned deficiency results for the case (populated on ingest).
      getLatestDeficiency(caseData.id)
        .then((j: any) => (j.results?.deficiencies || j.results || []) as DeficiencyDocResult[])
        .catch(() => [] as DeficiencyDocResult[]),
      getCaseChecklist(caseData.id).catch(() => null as CaseChecklist | null),
    ]).then(([d, def, chk]) => {
      if (cancelled) return;
      setDocs(d);
      setDefResults(def);
      setChecklist(chk);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [caseData.id, refreshKey]);

  const totalDeficiencies = defResults.reduce((n, r) => n + r.deficiencies.length, 0);
  const missingCount = checklist
    ? checklist.required_documents.filter((d) => d.status === 'missing').length +
      checklist.required_information.filter((d) => d.status === 'missing').length
    : 0;
  const displayDeficiencies = totalDeficiencies > 0 ? totalDeficiencies : missingCount;
  const isComparison = caseData.type === 'comparison';

  // Drag-and-drop document upload from the Overview Documents card.
  // Uses SERVER-SIDE reconstructFile() to detect PII-tagged files -- the
  // browser can't read REDACTED-{"<key>"} tokens out of a FlateDecode-compressed PDF
  // (the format produced by 'download stripped'), so client-side f.text()
  // detection misses them. When a tagged file is dropped, open the
  // Reconstruct PII popup with the repopulated text instead of uploading
  // it as a regular case document.
  const handleUploadFiles = async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    setUploadError(null);
    const taggedResults: { filename: string; text: string }[] = [];
    const regularFiles: File[] = [];
    for (const f of fileArr) {
      try {
        const result = await reconstructFile(caseData.id, f);
        if (result.reconstructed) {
          taggedResults.push({ filename: result.original_filename || f.name, text: result.reconstructed_text || '' });
        } else {
          regularFiles.push(f);
        }
      } catch {
        // 404 (no tag_map), extraction error, etc. -> upload as a regular
        // doc so the user still sees the file added to the case.
        regularFiles.push(f);
      }
    }
    // Reconstruct tagged files + show in the reconstruct popup.
    if (taggedResults.length > 0) {
      const first = taggedResults[0];
      setShowReconstruct(true);
      setReconstructError(null);
      setReconstructOutput('');
      setReconstructLoadedFile(
        taggedResults.length > 1
          ? `${first.filename} (+${taggedResults.length - 1} more)`
          : first.filename,
      );
      setReconstructing(true);
      try {
        // Combine multiple tagged files' reconstructed text with a
        // separator so a multi-file drop is fully visible in one popup.
        const combined = taggedResults
          .map((r) => (taggedResults.length > 1 ? `**${r.filename}**\n\n` : '') + r.text)
          .join('\n\n---\n\n');
        setReconstructOutput(combined || '(reconstruction yielded empty text)');
      } catch (e: any) {
        setReconstructError(e?.response?.data?.detail || e?.message || 'Reconstruct failed.');
      } finally {
        setReconstructing(false);
      }
    }
    // Upload regular (non-tagged) files as case documents.
    if (regularFiles.length > 0) {
      setUploading(true);
      try {
        await uploadDocuments(caseData.id, regularFiles);
        onDocumentsChanged?.();
      } catch (e: any) {
        setUploadError(e?.response?.data?.detail || e?.message || 'Upload failed.');
      } finally {
        setUploading(false);
      }
    }
  };

  // Run deficiency analysis on all case documents (UC-1.8 from Overview).
  const handleRunDeficiency = async () => {
    setDefError(null);
    const docIds = docs.map((d) => d.id);
    if (docIds.length === 0) {
      setDefError('No documents to analyze. Upload documents first.');
      return;
    }
    setRunningDef(true);
    try {
      const start = await startDeficiency(caseData.id, docIds, useLlm);
      onDeficiencyJob?.(start.job_id);
      // Poll until complete (awaited so the button stays in Running state).
      let done = false;
      for (let i = 0; i < 120 && !done; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const j: any = await getDeficiencyJob(start.job_id);
          if (j.status === 'complete' || j.status === 'error') {
            const results: any[] = j.results?.deficiencies || j.results || [];
            setDefResults(results as DeficiencyDocResult[]);
            if (j.status === 'error') setDefError('Deficiency analysis failed on the server.');
            done = true;
          }
        } catch { /* keep polling */ }
      }
      if (!done) setDefError('Deficiency analysis timed out.');
    } catch (e: any) {
      setDefError(e?.response?.data?.detail || e?.message || 'Failed to start deficiency analysis.');
    } finally {
      setRunningDef(false);
    }
  };

  // Download the deficiency list as a Word (.docx) doc. Uses the known
  // deficiencyJobId prop; falls back to getLatestDeficiency to discover one.
  const handleExportDeficiencyDocx = async () => {
    if (exportingDefDocx) return;
    setDefDocxError(null);
    setExportingDefDocx(true);
    try {
      let jobId = deficiencyJobId;
      if (!jobId) {
        try {
          const latest = await getLatestDeficiency(caseData.id);
          jobId = latest?.job_id || null;
        } catch { /* ignore -- surfaced below */ }
      }
      if (!jobId) {
        setDefDocxError('Run deficiency analysis first to enable .docx export.');
        return;
      }
      await downloadDeficiencyDocx(jobId, caseData.case_number || caseData.id);
    } catch (e: any) {
      setDefDocxError(e?.response?.data?.detail || e?.message || 'Failed to export deficiency .docx.');
    } finally {
      setExportingDefDocx(false);
    }
  };


  return (
    <div className="p-4 space-y-4" data-testid="overview-tab">
      {/* Case Facts — hidden for comparison cases (not deposit-review-specific) */}
      {!isComparison && (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-900">Case Facts</div>
          {editing ? (
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); resetEditFromCase(); }} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleSaveFacts} disabled={savingFacts} className="btn-primary text-sm">{savingFacts ? 'Saving...' : 'Save'}</button>
            </div>
          ) : (
            <button onClick={() => { resetEditFromCase(); setEditing(true); }} className="btn-secondary text-sm">Edit</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Issuer Name */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Issuer Name</span>
              <input value={editFacts.issuer_name} onChange={(e) => setEditFacts({ ...editFacts, issuer_name: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <FactField label="Issuer Name" value={caseData.issuer_name} manual={manualFields.has('issuer_name')} />}
          {/* Ticker */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ticker Symbol</span>
              <input value={editFacts.issuer_symbol} onChange={(e) => setEditFacts({ ...editFacts, issuer_symbol: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <IdentifierFactField label="Ticker Symbol" caseValue={caseData.ticker} factValue={factValueFor('issuer_symbol')} manual={manualFields.has('issuer_symbol')} fieldName="issuer_symbol" discrepancy={discrepancyFor('issuer_symbol')} />}
          {/* CUSIP */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CUSIP</span>
              <input value={editFacts.cusip} onChange={(e) => setEditFacts({ ...editFacts, cusip: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <IdentifierFactField label="CUSIP" caseValue={caseData.cusip} factValue={factValueFor('cusip')} mono manual={manualFields.has('cusip')} fieldName="cusip" discrepancy={discrepancyFor('cusip')} />}
          {/* CIK */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CIK</span>
              <input value={editFacts.cik} onChange={(e) => setEditFacts({ ...editFacts, cik: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <IdentifierFactField label="CIK" caseValue={caseData.cik} factValue={factValueFor('cik')} mono manual={manualFields.has('cik')} fieldName="cik" discrepancy={discrepancyFor('cik')} />}
          {/* Depositor Name */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Depositor Name</span>
              <input value={editFacts.depositor_name} onChange={(e) => setEditFacts({ ...editFacts, depositor_name: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <FactField label="Depositor Name" value={caseData.depositor_name} manual={manualFields.has('depositor_name')} />}
          {/* Depositor Type */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Depositor Type</span>
              <input value={editFacts.depositor_type} onChange={(e) => setEditFacts({ ...editFacts, depositor_type: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <FactField label="Depositor Type" value={caseData.depositor_type} manual={manualFields.has('depositor_type')} />}
          {/* Account Reference */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Account Reference</span>
              <input value={editFacts.account_ref} onChange={(e) => setEditFacts({ ...editFacts, account_ref: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <FactField label="Account Reference" value={caseData.account_ref} mono manual={manualFields.has('account_ref')} />}
          {/* Deposit Date */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Deposit Date</span>
              <input type="date" value={editFacts.deposit_date} onChange={(e) => setEditFacts({ ...editFacts, deposit_date: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <FactField label="Deposit Date" value={caseData.deposit_date} manual={manualFields.has('deposit_date')} />}
          {/* Share Quantity */}
          {editing ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Share Quantity</span>
              <input type="number" min="0" step="1" value={editFacts.share_quantity} onChange={(e) => setEditFacts({ ...editFacts, share_quantity: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
            </label>
          ) : <FactField label="Share Quantity" value={caseData.share_quantity != null ? caseData.share_quantity.toLocaleString() : undefined} manual={manualFields.has('share_quantity')} />}
        </div>
        {(onPullPublicData || onOpenPublicData || onRunReview || onReReview) && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {onPullPublicData && (
              <button
                onClick={onPullPublicData}
                className="btn-primary inline-flex items-center gap-1.5 text-sm"
                title="Fetch issuer information from EDGAR, OTC Markets, Yahoo, FINRA, OFAC, etc."
                data-testid="overview-pull-public-data"
              >
                <RefreshCw size={14} /> Pull Public Data
              </button>
            )}
            {onOpenPublicData && (
              <button
                onClick={onOpenPublicData}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                title="Open the Public Data tab"
                data-testid="overview-open-public-data"
              >
                <Database size={14} /> View Public Data
              </button>
            )}
            {/* Run review button cluster -- mirrors the old CaseDetail.tsx
                pattern. Gated on whether a review exists + whether one is
                in progress. */}
            {reviewInProgress && onCancelReview && (
              <button
                onClick={onCancelReview}
                disabled={cancellingReview}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm text-red-600 border-red-300 hover:bg-red-50"
                title="Cancel the in-progress review run"
                data-testid="overview-cancel-review"
              >
                {cancellingReview ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                Cancel Review
              </button>
            )}
            {hasReview ? (
              onRunReview && (
                <button
                  onClick={onRunReview}
                  disabled={reviewLoading || reviewInProgress}
                  className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                  title="Re-run the rubric review without pulling new public data"
                  data-testid="overview-rerun-review"
                >
                  {reviewLoading || reviewInProgress ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Re-run Review
                </button>
              )
            ) : (
              <>
                {onReReview && (
                  <button
                    onClick={onReReview}
                    disabled={rereviewLoading || reviewInProgress}
                    className="btn-primary inline-flex items-center gap-1.5 text-sm"
                    title="Pull public data (EDGAR, OTC, FINRA, OFAC, etc.) then run the rubric review"
                    data-testid="overview-pull-and-review"
                  >
                    {rereviewLoading || reviewInProgress ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
                    Pull Public Data &amp; Run Review
                  </button>
                )}
                {onRunReview && (
                  <button
                    onClick={onRunReview}
                    disabled={reviewLoading || reviewInProgress}
                    className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                    title="Run the rubric review without pulling new public data"
                    data-testid="overview-run-review"
                  >
                    {reviewLoading || reviewInProgress ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    Run Review
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {/* in-progress / stuck / failed-review banner */}
        {reviewInProgress && reviewStuck && onCancelReview && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-300 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600" />
            <span className="text-sm text-amber-800 font-medium flex-1">Review may be stuck (no progress for a while) &mdash; Cancel and re-run?</span>
            <button
              onClick={onCancelReview}
              disabled={cancellingReview}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm text-red-600 border-red-300 hover:bg-red-50"
              title="Cancel the stuck review run"
              data-testid="overview-stuck-cancel-review"
            >
              {cancellingReview ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
              Cancel
            </button>
          </div>
        )}
        {reviewInProgress && !reviewStuck && (
          <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin text-blue-600" />
            <span className="text-sm text-blue-800 font-medium">Review in progress...</span>
          </div>
        )}
        {!reviewInProgress && reviewFailedMsg && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-600" />
            <span className="text-sm text-red-800 font-medium">Review failed: {reviewFailedMsg}</span>
          </div>
        )}
      </div>
      )}

      {/* Highlights */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><FileText size={16} /> Documents: {loading ? <Loader2 size={14} className="animate-spin" /> : <span className="text-slate-800">{docs.length}</span>}</div>
          {/* Drop area for document ingest */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files.length > 0) handleUploadFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`mt-2 cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
              dragOver ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
            data-testid="overview-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,text/markdown"
              className="hidden"
              onChange={(e) => { if (e.target.files && e.target.files.length > 0) handleUploadFiles(e.target.files); e.target.value = ''; }}
            />
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Uploading...</div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-slate-400">
                <UploadCloud size={20} />
                <div className="text-xs">Drop documents here or click to upload</div>
              </div>
            )}
          </div>
          {uploadError && <div className="mt-1 text-xs text-red-600">{uploadError}</div>}
          <button
            onClick={() => { setShowReconstruct(true); setReconstructOutput(''); setReconstructError(null); setReconstructInput(''); }}
            className="btn-secondary inline-flex items-center gap-1.5 text-sm mt-2"
            title="Paste PII-stripped/tagged text and reconstruct the original PII from the case tag map"
            data-testid="overview-reconstruct"
          >
            <Unlock size={14} /> Reconstruct PII
          </button>
        </div>
        {/* Deficiencies card — hidden for comparison cases */}
        {!isComparison && (
        <div className="card p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><AlertTriangle size={16} /> Deficiencies: {loading ? <Loader2 size={14} className="animate-spin" /> : <span className="text-slate-800">{displayDeficiencies}</span>}</div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <button
              onClick={handleRunDeficiency}
              disabled={runningDef || loading || docs.length === 0}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              title="Rescan deficiencies on all case documents. Deterministic scan is fast; AI refine uses the local LLM."
              data-testid="overview-run-deficiency"
            >
              {runningDef ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {runningDef ? 'Running...' : 'Rescan deficiencies'}
            </button>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 select-none" title="Check to refine deficiencies into broker-forwardable language via the local LLM (slower).">
              <input
                type="checkbox"
                checked={useLlm}
                onChange={(e) => setUseLlm(e.target.checked)}
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-400"
                data-testid="overview-use-llm"
              />
              AI refine (slower)
            </label>
          </div>
          {/* Two popup buttons: email client + deficiency list */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              onClick={() => { setShowEmailClient(true); setCopied(false); }}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              title="Generate an email to the client listing missing documents and information"
              data-testid="overview-email-client"
            >
              <Mail size={14} /> Email client (missing docs)
            </button>
            <button
              onClick={() => { setShowDefList(true); setCopied(false); }}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              title="Show a nicely formatted deficiency list + missing items"
              data-testid="overview-def-list"
            >
              <FileText size={14} /> Deficiency list
            </button>
           </div>
          {defError && <div className="mt-1 text-xs text-red-600">{defError}</div>}
        </div>
        )}
      </div>

      {/* Comparison summary */}
      {compareSummary && (
        <div className="card p-4">
          <div className="text-sm font-semibold text-slate-900 mb-1">Comparison</div>
          <div className="text-sm text-slate-600">{compareSummary}</div>
        </div>
      )}

      {/* Required-documents + required-information checklist — hidden for comparison */}
      {!isComparison && checklist && (checklist.required_documents.length > 0 || checklist.required_information.length > 0) && (
        <div className="card p-4" data-testid="checklist-card">
          <div className="text-sm font-semibold text-slate-900 mb-3">Checklist</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Required Documents</div>
              <ul className="space-y-1">
                {checklist.required_documents.map((item, i) => (
                  <li key={'doc-' + i} className="flex items-start gap-2 text-sm">
                    <span className={
                      item.status === 'provided'
                        ? 'text-green-600 font-semibold'
                        : 'text-red-600 font-semibold'
                    }>{item.status === 'provided' ? '✓' : '✗'}</span>
                    <span className="text-slate-700">
                      {item.label}
                      {item.matched && <span className="text-xs text-slate-400"> ({item.matched})</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Required Information</div>
              <ul className="space-y-1">
                {checklist.required_information.map((item, i) => (
                  <li key={'info-' + i} className="flex items-start gap-2 text-sm">
                    <span className={
                      item.status === 'present'
                        ? 'text-green-600 font-semibold'
                        : 'text-red-600 font-semibold'
                    }>{item.status === 'present' ? '✓' : '✗'}</span>
                    <span className="text-slate-700">
                      {item.label}
                      {item.matched && <span className="text-xs text-slate-400"> ({item.matched})</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-400">
            {checklist.required_documents.filter((d) => d.status === 'missing').length} missing document(s) ·
            {' '}{checklist.required_information.filter((d) => d.status === 'missing').length} missing information item(s).
            Customize in Admin → Checklists.
          </div>
        </div>
      )}

      {/* Popup 1: email to client about missing documents — hidden for comparison */}
      {showEmailClient && !isComparison && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="email-client-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Mail className="text-primary-600" size={20} />
                <h2 className="text-lg font-semibold text-slate-900">Email to client — missing documents</h2>
              </div>
              <button onClick={() => setShowEmailClient(false)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <p className="text-xs text-slate-500 mb-2">Copy this email and paste it into your email client. Replace the bracketed placeholders with your details.</p>
              <textarea
                readOnly
                value={buildMissingDocsEmail(caseData, checklist)}
                className="w-full h-96 rounded-lg border border-slate-200 p-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-1 focus:ring-primary-400 resize-none"
                data-testid="email-client-body"
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-100">
              <button onClick={() => setShowEmailClient(false)} className="btn-secondary text-sm">Close</button>
              <button
                onClick={async () => {
                  const ok = await copyToClipboard(buildMissingDocsEmail(caseData, checklist));
                  if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
                }}
                className="btn-primary inline-flex items-center gap-1.5 text-sm"
                data-testid="email-client-copy"
              >
                <Copy size={14} /> {copied ? 'Copied!' : 'Copy to clipboard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup 2: nicely formatted deficiency list + missing items — hidden for comparison */}
      {showDefList && !isComparison && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="def-list-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileText className="text-primary-600" size={20} />
                <h2 className="text-lg font-semibold text-slate-900">Deficiency list + missing items</h2>
              </div>
              <button onClick={() => setShowDefList(false)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="mb-3 flex items-start gap-2 text-xs text-slate-500" data-testid="ai-disclaimer">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>AI-generated — AI can make mistakes. Verify before relying.</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">Copy this report and paste it into your notes or an email. Run the deficiency analysis first for document-level findings.</p>
              <div
                className="prose prose-sm max-w-none text-slate-800 h-96 overflow-y-auto rounded-lg border border-slate-200 p-3"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(buildDeficiencyReport(caseData, docs, defResults, checklist)) }}
                data-testid="def-list-body"
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-100">
              <button onClick={() => setShowDefList(false)} className="btn-secondary text-sm">Close</button>
              <button
                onClick={handleExportDeficiencyDocx}
                disabled={exportingDefDocx}
                title={
                  deficiencyJobId
                    ? 'Download the deficiency list as a Word (.docx) file'
                    : 'Download the latest deficiency list as a Word (.docx) file (run deficiency analysis first if none exists)'
                }
                className="btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
                data-testid="def-list-export-docx"
              >
                {exportingDefDocx ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}{' '}
                Download .docx
              </button>
              <button
                onClick={async () => {
                  const ok = await copyToClipboard(buildDeficiencyReport(caseData, docs, defResults, checklist));
                  if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
                }}
                className="btn-primary inline-flex items-center gap-1.5 text-sm"
                data-testid="def-list-copy"
              >
                <Copy size={14} /> {copied ? 'Copied!' : 'Copy to clipboard'}
              </button>
            </div>
            {defDocxError && (
              <div className="px-6 pb-3 text-xs text-red-600">{defDocxError}</div>
            )}
          </div>
        </div>
      )}

      {/* Popup 3: reconstruct PII from tagged text */}
      {showReconstruct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="reconstruct-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Unlock className="text-primary-600" size={20} />
                <h2 className="text-lg font-semibold text-slate-900">Reconstruct PII from tagged text</h2>
              </div>
              <button onClick={() => setShowReconstruct(false)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
              <p className="text-xs text-slate-500">Load a PII-stripped / data-washed document (PDF, DOCX, TXT, or MD -- the same files you download with "Download washed") OR paste the tagged text below. The system replaces the REDACTED-{"<key>"} tokens with the original PII values from this case's tag map.</p>
              {/* Load a stripped document file -- same reconstruct-file endpoint
                  the chat uses; the dialog is an alternate UI for the same flow. */}
              <input ref={reconFileInputRef} type="file" multiple={false}
                accept=".pdf,.doc,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,text/markdown"
                className="hidden" onChange={onReconFilePicked} data-testid="reconstruct-file-input" />
              <button
                onClick={() => reconFileInputRef.current?.click()}
                disabled={reconstructing}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                title="Load a PII-stripped document file (PDF/DOCX/TXT/MD) and reconstruct it"
                data-testid="reconstruct-load-file"
              >
                {reconstructing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                Load stripped document
              </button>
              {reconstructLoadedFile && !reconstructing && (
                <div className="text-xs text-slate-500">Loaded: <span className="font-medium text-slate-700">{reconstructLoadedFile}</span></div>
              )}
              <div className="relative flex items-center my-2">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="px-3 text-[11px] uppercase tracking-wide text-slate-400">or paste text</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Tagged text (input)</label>
                <textarea
                  value={reconstructInput}
                  onChange={(e) => setReconstructInput(e.target.value)}
                  placeholder="Paste stripped text with tags here..."
                  className="w-full h-32 rounded-lg border border-slate-200 p-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-1 focus:ring-primary-400 resize-none"
                  data-testid="reconstruct-input"
                />
              </div>
              {reconstructError && <div className="text-xs text-red-600">{reconstructError}</div>}
              <button
                onClick={handleReconstruct}
                disabled={reconstructing || !reconstructInput.trim()}
                className="btn-primary inline-flex items-center gap-1.5 text-sm"
                data-testid="reconstruct-btn"
              >
                {reconstructing ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
                {reconstructing ? 'Reconstructing...' : 'Reconstruct'}
              </button>
              {reconstructOutput && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Reconstructed text (output)</label>
                  <textarea
                    readOnly
                    value={reconstructOutput}
                    className="w-full h-40 rounded-lg border border-slate-200 p-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-1 focus:ring-primary-400 resize-none"
                    data-testid="reconstruct-output"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-100">
              <button onClick={() => setShowReconstruct(false)} className="btn-secondary text-sm">Close</button>
              {reconstructOutput && (
                <>
                  <button
                    onClick={handleReconstructDownload}
                    className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                    data-testid="reconstruct-download"
                    title="Download the reconstructed text as a .md file"
                  >
                    <Download size={14} /> Download .md
                  </button>
                  <button
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(reconstructOutput); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
                    }}
                    className="btn-primary inline-flex items-center gap-1.5 text-sm"
                    data-testid="reconstruct-copy"
                  >
                    <Copy size={14} /> {copied ? 'Copied!' : 'Copy result'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// FactField — same layout as the original CaseDetail version, with an
// asterisk (*) for manually-overridden fields.
function FactField({ label, value, mono, manual }: { label: string; value?: string | null; mono?: boolean; manual?: boolean }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
        {label}{manual && <span className="text-amber-500 ml-0.5" title="Manually edited">*</span>}
      </p>
      <p className={`text-sm text-slate-700 ${mono ? 'font-mono' : ''}`}>{value || '--'}</p>
    </div>
  );
}

// IdentifierFactField — gated identifier (CUSIP / CIK / Ticker) with an
// amber "Rejected" badge when the intake identifier gate rejected the
// form value (operator 2026-11-04). Shows the rejected value + reason in
// a tooltip and surfaces the "manual-review-required" placeholder as the
// field value so the field is not silently blank.
const REJECTION_REASON_MAP: Record<string, string> = {
  cusip: 'CUSIP check-digit invalid',
  cik: 'SEC file number, not a CIK',
  issuer_symbol: 'form label word, not a ticker',
};
function IdentifierFactField({
  label,
  caseValue,
  factValue,
  mono,
  manual,
  fieldName,
  discrepancy,
}: {
  label: string;
  caseValue?: string | null;
  factValue?: string | null;
  mono?: boolean;
  manual?: boolean;
  fieldName: string;
  discrepancy?: IntakeDiscrepancy;
}) {
  const rejected = Boolean(discrepancy && discrepancy.conflicting_value);
  const rejectedValue = discrepancy?.conflicting_value || '';
  const reason = REJECTION_REASON_MAP[fieldName] || 'failed identifier validation';
  // When the Case header column is blank but the CaseFact carries the
  // manual-review placeholder, show the placeholder instead of '--'.
  const displayValue = caseValue || (factValue === 'manual-review-required' ? 'manual-review-required' : '');
  const tooltip = rejected
    ? `Rejected: ${rejectedValue} — ${reason}. Flagged for manual review.`
    : undefined;
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
        {label}{manual && <span className="text-amber-500 ml-0.5" title="Manually edited">*</span>}
        {rejected && (
          <span
            className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700"
            title={tooltip}
          >
            Rejected
          </span>
        )}
      </p>
      <p
        className={`text-sm text-slate-700 ${mono ? 'font-mono' : ''} ${rejected ? 'cursor-help' : ''}`}
        title={tooltip}
      >
        {displayValue || '--'}
      </p>
    </div>
  );
}